import React, {Component} from 'react';
import {fetchStats, formatCurrency, retrieveFilenameFromHeaders, sendFileToBrowser} from '../services';
import googleChartLoader from '../GoogleChartLoader';
import Loader from '../Elements/Loader';
import {Button, ButtonGroup, Row, Col} from 'reactstrap';
import FetchFromContext from "../Elements/FetchFromContext";
import Select from 'react-virtualized-select';
import {DesktopDownloadIcon} from "react-octicons";
import moment from "moment";

class TransactionCompletedStats extends Component {

    constructor(props) {
        super(props);

        this.state = {
            isCommunicatingWithServer: false,
            showError: false,
            rows: [],
            groupBy: 'day',
            billingSenderIds: [],
            parentSenderIds: [],
            paymentMethod: null,
            height: this.props.height ? parseInt(this.props.height, 10) : 400,
        };

        this.drawDashboard = this.drawDashboard.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleChangeSenders = this.handleChangeSenders.bind(this);
        this.handleChangeParentSenders = this.handleChangeParentSenders.bind(this);
        this.handleChangePaymentMethod = this.handleChangePaymentMethod.bind(this);
        this.handleDownload = this.handleDownload.bind(this);
        this.fetchData = this.fetchData.bind(this);
    }

    componentDidMount() {
        googleChartLoader.init(['corechart', 'controls']);
        this.fetchData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.groupBy !== this.state.groupBy) {
            this.fetchData();
            return;
        }

        if (JSON.stringify(prevState.billingSenderIds) !== JSON.stringify(this.state.billingSenderIds)) {
            this.fetchData();
            return;
        }

        if (JSON.stringify(prevState.parentSenderIds) !== JSON.stringify(this.state.parentSenderIds)) {
            this.fetchData();
            return;
        }

        if (prevState.paymentMethod !== this.state.paymentMethod) {
            this.fetchData();
            return;
        }

        if (prevState.rows.length !== this.state.rows.length) {
            if (!this.state.isCommunicatingWithServer || this.state.rows.length === 0) {
                googleChartLoader.init().then(() => {
                    this.drawDashboard();
                });
            }
        }
    }

    fetchData() {
        this.setState({
            isCommunicatingWithServer: true,
            showError: false,
            rows: [],
        });

        fetchStats('transactionCompleted', {
            groupBy: this.state.groupBy,
            billingSenderIds: this.state.billingSenderIds,
            parentSenderIds: this.state.parentSenderIds,
            paymentMethods: this.state.paymentMethod ? [this.state.paymentMethod] : [],
        }).then(data => {
            const processedData = data.map(item => [
                new Date(item[0]),
                item[1], //total
                `${moment(item[0]).format('DD/MM/YYYY')}\nTransactions completed: ${item[1]}\nTotal amount: ${formatCurrency(item[2])}`, //Amount tooltip
            ]);
            this.setState({
                rows: processedData,
                isCommunicatingWithServer: false
            });
        }).catch((errorMessage) => {
            this.setState({
                isCommunicatingWithServer: false,
                showError: true,
            });
        });
    }

    handleChangeSenders(items) {
        this.setState({
            billingSenderIds: items.map(item => item.value),
        });
    }

    handleChangeParentSenders(items) {
        this.setState({
            parentSenderIds: items.map(item => item.value),
        });
    }

    handleChangePaymentMethod(item) {
        this.setState({
            paymentMethod: item?.value,
        });
    }

    handleChange(attribute) {
        return value => {
            this.setState({
                [attribute]: value,
            });
        };
    }

    async handleDownload() {
        const response = await fetchStats('transactionCompleted', {
            groupBy: this.state.groupBy,
            billingSenderIds: this.state.billingSenderIds,
            parentSenderIds: this.state.parentSenderIds,
            paymentMethod: this.state.paymentMethod ? [this.state.paymentMethod] : [],
            format: 'xlsx',
        }, true);

        if (response.status === 400) {
            const data = await response.json();
            throw new Error(data.message);
        }

        if (response.status !== 200) {
            throw new Error(`${response.status}: ${response.statusText}. Contact a site administrator. This error probably won't go away by itself.`);
        }

        //fallback filename if no filename from server is received
        let filename = retrieveFilenameFromHeaders(response.headers, 'TransactionCompletedStats.xlsx');

        const blob = await response.blob();
        sendFileToBrowser(filename, blob);
    }

    drawDashboard() {
        const data = new window.google.visualization.DataTable();
        data.addColumn("date", 'Date');
        data.addColumn("number", 'Transactions completed');
        data.addColumn({type: 'string', role: 'tooltip'});
        data.addRows(this.state.rows);

        const dashboard = new window.google.visualization.Dashboard(document.getElementById('TransactionCompletedStatsWrapper'));

        let end = this.state.rows.length > 0 ? this.state.rows[this.state.rows.length - 1][0] : new Date();
        let start = new Date(end);
        let format = 'dd/MM/yy';

        switch (this.state.groupBy) {
            case 'month':
                start.setFullYear(start.getFullYear() - 2);
                format = 'MMM y';
                break;
            case 'quarter':
                start.setFullYear(start.getFullYear() - 2);
                format = 'y Q';
                break;
            case 'year':
                start = this.state.rows.length > 0 ? this.state.rows[0][0] : new Date(); //assume that all rows can be shown when grouped by year
                format = 'y';
                break;
            default:
                start.setMonth(start.getMonth() - 2);
        }

        const chart = new window.google.visualization.ChartWrapper({
            chartType: 'ColumnChart',
            containerId: 'TransactionCompletedStats',
            options: {
                title: 'Transaction Completed',
                legend: {
                    position: 'none',
                },
                height: this.state.height,
                chartArea: {
                    left: 40,
                    right: 0,
                    top: 30,
                    bottom: 30,
                },
                hAxis: {
                    format: format,
                },
                isStacked: true,
            },
        });

        const controls = new window.google.visualization.ControlWrapper({
            controlType: 'ChartRangeFilter',
            containerId: 'TransactionCompletedStatsControls',
            options: {
                filterColumnLabel: 'Date',
                ui: {
                    chartType: 'AreaChart',
                    chartOptions: {
                        isStacked: true,
                        height: 60,
                        chartArea: {
                            left: 40,
                            right: 10,
                            top: 0,
                            bottom: 20,
                        },
                        hAxis: {
                            'textPosition': 'out',
                            format: format,
                        },
                    },
                },
            },
            state: {
                range: {
                    start: start,
                    end: end,
                }
            },
        });

        new window.google.visualization.DateFormat({pattern: format}).format(data, 0);
        dashboard.bind([controls], [chart]);
        dashboard.draw(data);
    }

    render() {

        const height = this.state.height + 60;

        return (
            <div id="TransactionCompletedStatsWrapper">
                <Row form className="justify-content-end">
                    <Col style={{maxWidth: 400, marginBottom: 10}}>
                        <FetchFromContext property="transactionPaymentMethods">
                            {paymentMethods => (
                                <Select
                                    placeholder="Payment methods"
                                    value={this.state.paymentMethod}
                                    options={paymentMethods.map(paymentMethod => ({
                                        value: paymentMethod.id,
                                        label: paymentMethod.name,
                                    }))}
                                    onChange={this.handleChangePaymentMethod}
                                />
                            )}
                        </FetchFromContext>
                    </Col>
                    <Col style={{marginBottom: 10}}>
                        <FetchFromContext property="billingSenders">
                            {billingSenders => (
                                <Select
                                    placeholder="Billing senders"
                                    value={this.state.billingSenderIds}
                                    options={billingSenders.map(billingSender => ({
                                        value: billingSender.id,
                                        label: billingSender.name,
                                    }))}
                                    onChange={this.handleChangeSenders}
                                    multi
                                />
                            )}
                        </FetchFromContext>
                    </Col>
                    <Col style={{marginBottom: 10}}>
                        <FetchFromContext property="parentSenders">
                            {parentSenders => (
                                <Select
                                    placeholder="Parent senders"
                                    value={this.state.parentSenderIds}
                                    options={parentSenders.map(parentSender => ({
                                        value: parentSender.id,
                                        label: parentSender.name,
                                    }))}
                                    onChange={this.handleChangeParentSenders}
                                    multi
                                />
                            )}
                        </FetchFromContext>
                    </Col>
                </Row>
                <Row form className="justify-content-end">
                    <Col xs="auto" style={{marginBottom: 10}}>
                        <ButtonGroup size="sm">
                            <Button outline disabled={this.state.isCommunicatingWithServer} active={this.state.groupBy === 'day'} color="success" onClick={() => this.handleChange('groupBy')('day')}>Day</Button>
                            <Button outline disabled={this.state.isCommunicatingWithServer} active={this.state.groupBy === 'month'} color="success" onClick={() => this.handleChange('groupBy')('month')}>Month</Button>
                            <Button outline disabled={this.state.isCommunicatingWithServer} active={this.state.groupBy === 'quarter'} color="success" onClick={() => this.handleChange('groupBy')('quarter')}>Quarter</Button>
                            <Button outline disabled={this.state.isCommunicatingWithServer} active={this.state.groupBy === 'year'} color="success" onClick={() => this.handleChange('groupBy')('year')}>Year</Button>
                        </ButtonGroup>
                    </Col>
                    <Col xs="auto">
                        <DesktopDownloadIcon onClick={this.handleDownload} style={{cursor: 'pointer'}}/>
                    </Col>
                </Row>
                <div style={{height: height + 'px'}}>
                    {this.state.isCommunicatingWithServer &&
                    <div className="d-flex flex-column justify-content-center h-100">
                        <Loader/>
                    </div>
                    }
                    {this.state.showError &&
                    <div className="d-flex flex-column justify-content-center h-100 text-center">
                        An error occured showing this graph. Try refreshing the page.
                    </div>
                    }
                    <div id="TransactionCompletedStats"/>
                    <div id="TransactionCompletedStatsControls"/>
                </div>
            </div>
        );

    }
}

export default TransactionCompletedStats;
